import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Typography } from "@mui/joy";
import FullPageContainer from "pages/container/FullPageContainer";
import { theme } from "styles/theme";
import useTaskEditorStore from "hooks/useTaskEditorStore";
import { taskEditorApi } from "api/taskEditorApi";
import {
  getWorkingTask,
  upsertWorkingTask,
  WorkingTaskVersionConflictError,
} from "api/taskWorkingApi";
import {
  cloneTaskEditorState,
  createEmptyTaskEditorState,
  stableHashBlocks,
} from "./utils/taskEditorDiff";
import {
  type TaskEditorState,
  type TaskMetaUpdate,
  UNCATEGORIZED_SET_NUMBER,
} from "./utils/taskEditorUtils";
import CustomModal from "components/common/CustomModal";
import { useTaskEditorSyncState } from "./utils/useTaskEditorSyncState";
import AgentChatPanel from "./AgentChatPanel";
import AgentDebugPanel from "./agent/AgentDebugPanel";
import { ConflictBanner } from "./agent/ConflictBanner";
import TaskListPanel from "./TaskListPanel";
import {
  computeListReorderUpdates,
  getKeyActivitySetNumbers,
  getNextSortOrderForSet,
  getTaskEditorSetSectionLabel,
} from "./utils/taskEditorListUtils";
import TaskMetadataModal, { TaskMetadataValues } from "./TaskMetadataModal";
import TaskWorkspacePanel from "./TaskWorkspacePanel";

type MetaModalState =
  | { mode: "create" }
  | {
      mode: "edit";
      initialValues: TaskMetadataValues;
      originalTaskId: string;
    };

const TASK_LIST_WIDTH_MIN = 160;
const TASK_LIST_WIDTH_MAX = 240;
const TASK_LIST_WIDTH_DEFAULT = 160;
const TASK_LIST_WIDTH_STORAGE_KEY = "taskEditorTaskListWidth";
const RESIZE_HANDLE_WIDTH = 8;
const CHAT_MIN_WIDTH = 280;
const WORKSPACE_WIDTH_MIN = 420;
const WORKSPACE_WIDTH_MAX = 1600;
const WORKSPACE_WIDTH_DEFAULT = 480;
const WORKSPACE_WIDTH_STORAGE_KEY = "taskEditorWorkspaceWidth";

function loadInitialWorkspaceWidth(): number {
  if (typeof window === "undefined") {
    return WORKSPACE_WIDTH_DEFAULT;
  }
  try {
    const saved = window.localStorage.getItem(WORKSPACE_WIDTH_STORAGE_KEY);
    if (!saved) return WORKSPACE_WIDTH_DEFAULT;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return WORKSPACE_WIDTH_DEFAULT;
    return Math.min(Math.max(parsed, WORKSPACE_WIDTH_MIN), WORKSPACE_WIDTH_MAX);
  } catch {
    return WORKSPACE_WIDTH_DEFAULT;
  }
}

function loadInitialTaskListWidth(): number {
  if (typeof window === "undefined") {
    return TASK_LIST_WIDTH_DEFAULT;
  }
  try {
    const saved = window.localStorage.getItem(TASK_LIST_WIDTH_STORAGE_KEY);
    if (!saved) return TASK_LIST_WIDTH_DEFAULT;
    const parsed = parseInt(saved, 10);
    if (!Number.isFinite(parsed)) return TASK_LIST_WIDTH_DEFAULT;
    return Math.min(Math.max(parsed, TASK_LIST_WIDTH_MIN), TASK_LIST_WIDTH_MAX);
  } catch {
    return TASK_LIST_WIDTH_DEFAULT;
  }
}

export default function TaskEditorPage() {
  const {
    tasksByTaskId,
    isStoreHydrated,
    applyTaskBlocks,
    saveTaskMeta,
    reorderTasks,
    removeTask,
  } = useTaskEditorStore();

  // task/detail 로 받은 content 원본(비-blocks 필드 포함)을 task_id 별로 캐시.
  // 본문 저장(commit) 시 blocks 만 갈아끼우고 나머지(chapters 등)는 그대로 passthrough.
  const contentCacheRef = useRef<Record<string, Record<string, unknown>>>({});

  const tasksList = useMemo(
    () => Object.values(tasksByTaskId),
    [tasksByTaskId]
  );

  const [activeTaskId, setActiveTaskId] = useState<string | undefined>(
    undefined
  );
  const sync = useTaskEditorSyncState();
  const currentEditorState = sync.currentTask;
  const setCurrentEditorState = sync.setInitial;
  // detail(V1 본문) 로드가 끝난 활동지 id 집합. "로드 끝났는가" 를 task_id 별로 추적해
  // (1) 로드 전엔 placeholder([header] 폴백 = "헤더 텍스트를 입력하세요") 대신 skeleton 을 보이고,
  // (2) 재방문 시엔 이미 들어있어 skeleton 없이 캐시 본문을 즉시 보인다. skeleton 여부는 이 Set
  // 에서 *렌더 중 파생* 하므로 setCurrentEditorState/applyTaskBlocks 갱신 순서와 무관 — placeholder
  // 가 한 프레임도 새지 않는다. (단일 hydratedTaskId + 별도 skeleton 플래그는 타이밍이 어긋나 샜다.)
  // 또한 hasUnsavedChanges 의 "로드 전 비교 안 함" 게이트(버튼 깜빡임 방지)도 이 Set 으로 일원화.
  const [detailLoadedTaskIds, setDetailLoadedTaskIds] = useState<Set<string>>(
    () => new Set()
  );
  const markDetailLoaded = useCallback((taskId: string) => {
    setDetailLoadedTaskIds((prev) =>
      prev.has(taskId) ? prev : new Set(prev).add(taskId)
    );
  }, []);
  // 커밋 필요 여부는 명령형 플래그가 아니라 "현재 본문 ≠ 커밋된 V1" 파생값이다.
  // V1 base = tasksByTaskId[id].blocks (applyTaskBlocks 가 hydrate·커밋 시 reactive 갱신).
  // 명령형 set 은 편집 되돌림/no-op patch/revision 불일치에서 실제 diff 와 어긋나
  // 버튼이 제멋대로 활성/비활성되던 원인 — TaskPatchPill 과 동일한 해시 비교로 일원화.
  const hasUnsavedChanges = useMemo(() => {
    if (!activeTaskId || !currentEditorState) return false;
    // V1 detail 로드 전(과도구간)엔 비교하지 않는다 — 깜빡임 방지.
    if (!detailLoadedTaskIds.has(activeTaskId)) return false;
    const v1 = tasksByTaskId[activeTaskId];
    if (!v1) return false;
    return (
      stableHashBlocks(currentEditorState.blocks) !==
      stableHashBlocks(v1.blocks)
    );
  }, [activeTaskId, currentEditorState, tasksByTaskId, detailLoadedTaskIds]);
  // 우측 패널 표시 모드(파생). 활동지 0개 → 빈 안내, 로딩(초기/전환 detail 미로드) → skeleton,
  // 그 외 → 에디터. detailLoadedTaskIds 에서 렌더 중 파생하므로 갱신 순서와 무관(타이밍 안전).
  const showNoTasks = isStoreHydrated && tasksList.length === 0;
  const isWorkspaceHydrating = showNoTasks
    ? false
    : !activeTaskId
      ? true
      : !detailLoadedTaskIds.has(activeTaskId);
  // 백그라운드 포함 스트리밍 중인 활동지 id — 목록의 진행 인디케이터에 사용.
  const [streamingTaskIds, setStreamingTaskIds] = useState<string[]>([]);
  // 턴 종료 reconcile 이 await 도중의 최신 값을 동기 읽기 위한 ref 미러.
  const activeTaskIdRef = useRef(activeTaskId);
  activeTaskIdRef.current = activeTaskId;
  const currentEditorStateRef = useRef(currentEditorState);
  currentEditorStateRef.current = currentEditorState;
  // cancel + consumeStream finally 가 같은 턴 종료에서 onTurnEnd 를 중복 호출할 때 GET 중복 방지.
  const reconcileInFlightRef = useRef(false);
  const [metaModal, setMetaModal] = useState<MetaModalState | null>(null);
  // 드래그로 세트가 바뀔 때 사용자 confirm 을 받기 위한 대기 상태.
  const [pendingSetChange, setPendingSetChange] = useState<{
    updates: TaskMetaUpdate[];
    taskTitle: string;
    fromSet: number;
    toSet: number;
    // 타깃 세트에 이미 Key 활동지가 있어 이동 활동지의 Key 가 해제되는 경우.
    demoteKey: boolean;
  } | null>(null);
  const [isSavingMeta, setIsSavingMeta] = useState(false);
  const [isCommittingContent, setIsCommittingContent] = useState(false);
  const [workspaceWidth, setWorkspaceWidth] = useState<number>(
    loadInitialWorkspaceWidth
  );
  const [isResizingWorkspace, setIsResizingWorkspace] = useState(false);
  const [taskListWidth, setTaskListWidth] = useState<number>(
    loadInitialTaskListWidth
  );
  const [isResizingTaskList, setIsResizingTaskList] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        WORKSPACE_WIDTH_STORAGE_KEY,
        String(workspaceWidth)
      );
    } catch {
      // ignore
    }
  }, [workspaceWidth]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        TASK_LIST_WIDTH_STORAGE_KEY,
        String(taskListWidth)
      );
    } catch {
      // ignore
    }
  }, [taskListWidth]);

  const handleWorkspaceResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = workspaceWidth;
    const dynamicMax = Math.min(
      WORKSPACE_WIDTH_MAX,
      Math.max(
        WORKSPACE_WIDTH_MIN,
        window.innerWidth - taskListWidth - CHAT_MIN_WIDTH
      )
    );
    setIsResizingWorkspace(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const next = Math.min(
        Math.max(startWidth + delta, WORKSPACE_WIDTH_MIN),
        dynamicMax
      );
      setWorkspaceWidth(next);
    };

    const handleEnd = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizingWorkspace(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const handleTaskListResizeStart = (event: React.MouseEvent) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = taskListWidth;
    const dynamicMax = Math.min(
      TASK_LIST_WIDTH_MAX,
      Math.max(
        TASK_LIST_WIDTH_MIN,
        window.innerWidth - CHAT_MIN_WIDTH - workspaceWidth
      )
    );
    setIsResizingTaskList(true);

    const handleMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      const next = Math.min(
        Math.max(startWidth + delta, TASK_LIST_WIDTH_MIN),
        dynamicMax
      );
      setTaskListWidth(next);
    };

    const handleEnd = () => {
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      setIsResizingTaskList(false);
    };

    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleEnd);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!activeTaskId && tasksList.length > 0) {
      setActiveTaskId(tasksList[0].task_id);
    }
  }, [activeTaskId, tasksList]);

  // V1 base 주입 + working_task hydrate — 하나의 effect 로 통합. 두 effect 로 분리하면 두 번째
  // (hydrate) 의 closure 가 첫 번째의 setCurrentEditorState 직전 값 (undefined) 을 봐 early return
  // 되고, deps 가 [activeTaskId] 라 재실행도 안 됨. 새로고침 후 working 이 영원히 hydrate 안 되는
  // race. 단일 effect 안에서 task 변수를 closure 로 캡쳐하면 state 갱신 timing 과 무관.
  //
  // 3 가지 케이스 분기:
  //   1. working 없음 → V1 그대로 (버전 미기록 → 0 → 첫 PUT 은 새 row 만들기)
  //   2. working.revision == V1 hash → 동기화된 정상 상태. V1 표시, row 유지 (DELETE 안 함)
  //   3. working.revision != V1 hash → working 채택 (현재 본문 ≠ V1 → 파생 hasUnsavedChanges 가 헤더 우측 노출)
  // working.version 은 task_id 별 ref 맵에 캐시해 이후 PUT 의 expected_version 으로 사용
  // (optimistic). 단일 ref 가 아니라 맵인 이유: 활동지를 빠르게 전환하면 직전 활동지의 debounce
  // PUT 이 늦게 발사되는데, 단일 ref 면 그 사이 현재 활동지 버전으로 덮여 잘못된 expected_version
  // 으로 409 → 다른 활동지 blocks 가 현재 에디터로 머지되는 덮어쓰기가 났다. task_id 로 분리.
  const workingVersionsRef = useRef<Record<string, number>>({});
  useEffect(() => {
    if (!activeTaskId) {
      setCurrentEditorState(undefined);
      return;
    }
    const meta = tasksByTaskId[activeTaskId];
    if (!meta) {
      setCurrentEditorState(undefined);
      return;
    }
    // 활동지 전환 시점에 직전 활동지의 (백그라운드로 넘어간) 턴 base 를 비운다. sync 는
    // 활성 에디터 1개를 위한 단일 인스턴스라, 이전 활동지에서 markTurnStart 후 전환되면
    // 그 base 가 남는다. 새 활성 활동지의 agent patch 는 base 부재 fallback(as-is 적용)을
    // 타도록 비워, 다른 활동지의 base 로 잘못 3-way merge 되는 것을 막는다.
    sync.markTurnEnd();
    // 1차: 메타만으로 즉시 표시 (list 에는 blocks 가 없어 본문은 비어있을 수 있음).
    // detail 미로드 활동지는 detailLoadedTaskIds 에 없어 본문 대신 skeleton 이 뜬다 — placeholder
    // ([header] 폴백) 가 노출되지 않는다. (재방문이면 이미 Set 에 있어 이 clone(캐시 본문)이 바로 보임.)
    setCurrentEditorState(cloneTaskEditorState(meta));
    workingVersionsRef.current[activeTaskId] = 0;

    let cancelled = false;
    void (async () => {
      try {
        // 2차: task/detail 로 본문(content.blocks) 로드 → V1 base 확정.
        const detail = await taskEditorApi.getTaskDetail(activeTaskId);
        if (cancelled) return;
        const content =
          detail && detail.content && typeof detail.content === "object"
            ? (detail.content as Record<string, unknown>)
            : {};
        // content 원본 캐시 (commit 시 비-blocks 필드 passthrough 용).
        contentCacheRef.current[activeTaskId] = content;
        // blocks 는 migrate 를 거쳐 정규화. v1Task 가 codeview/turnView/working base.
        const v1Task = cloneTaskEditorState({
          ...meta,
          blocks: Array.isArray(content.blocks) ? content.blocks : [],
        });
        applyTaskBlocks(activeTaskId, v1Task.blocks);
        setCurrentEditorState(cloneTaskEditorState(v1Task));
        // detail 로드 완료 — 이 활동지를 "로드됨" 으로 표기. setCurrentEditorState(실제 본문)
        // *뒤에* 호출하므로 Set 에 드는 렌더에서는 항상 실제 본문이 보장된다(placeholder 차단).
        // 이때부터 버튼 상태 비교(hasUnsavedChanges)도 활성화된다.
        markDetailLoaded(activeTaskId);

        // 3차: working_task hydrate (base = 방금 받은 V1 blocks).
        const working = await getWorkingTask(activeTaskId);
        if (cancelled || !working) return;
        workingVersionsRef.current[activeTaskId] = working.version;
        const v1Hash = stableHashBlocks(v1Task.blocks);
        // working_store(BE)는 action:null 같은 필드를 생략(lean)해 직렬화하므로 working.blocks 를
        // raw 로 비교·채택하면 migrate 형식(action:null 명시)인 V1 과 영구 불일치한다 — 버튼이
        // 무편집인데 활성으로 고착된다. migrate 로 정규화한 뒤 비교·채택해 표현 차이를 제거한다.
        const workingTask = cloneTaskEditorState({
          ...v1Task,
          blocks: working.blocks,
        });
        if (stableHashBlocks(workingTask.blocks) === v1Hash) {
          return;
        }
        sync.applyUserMutation(workingTask);
      } catch {
        // 로드 실패는 무시 — UI 는 1차 메타 상태 유지
      }
    })();
    return () => {
      cancelled = true;
    };
    // tasksByTaskId 변화는 의도적으로 의존성에서 제외 — 사용자 편집 중 외부 atom 갱신이
    // currentEditorState 를 덮어쓰지 않도록.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTaskId]);

  // 편집 시 debounce 후 PUT working — signature 캐시로 unchanged 시 skip.
  // 409 (다른 actor 가 사이에 변경) 시 store 의 current_blocks 로 sync.applyAgentPatch → ConflictBanner.
  const lastPutSignatureRef = useRef<{ taskId: string; sig: string } | null>(
    null
  );
  const putTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleWorkingPut = useCallback(
    (taskId: string, blocks: TaskEditorState["blocks"]) => {
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
      putTimerRef.current = setTimeout(() => {
        const sig = stableHashBlocks(blocks);
        const prev = lastPutSignatureRef.current;
        if (prev && prev.taskId === taskId && prev.sig === sig) return;
        lastPutSignatureRef.current = { taskId, sig };
        void (async () => {
          try {
            const saved = await upsertWorkingTask(
              taskId,
              blocks,
              workingVersionsRef.current[taskId] ?? 0,
              "user"
            );
            workingVersionsRef.current[taskId] = saved.version;
          } catch (err) {
            if (err instanceof WorkingTaskVersionConflictError) {
              // 다른 actor (agent 또는 다른 기기 사용자) 가 사이에 변경 — current_blocks 를 theirs 로 보고
              // 3-way merge 수행. 같은 block 둘 다 만진 경우 ConflictBanner 자동 표시.
              // 단, 이 PUT 발사 전 활동지를 전환했다면(늦은 PUT) 충돌 blocks 는 다른 활동지 것이므로
              // 현재 에디터에 적용하면 안 된다 — 활성 활동지일 때만 머지한다.
              workingVersionsRef.current[taskId] = err.current.version;
              if (activeTaskIdRef.current === taskId) {
                sync.applyAgentPatch({ blocks: err.current.blocks });
              }
              lastPutSignatureRef.current = null;
            } else {
              // network / 기타 실패 — 다음 변경 시 자연 재시도
              lastPutSignatureRef.current = null;
            }
          }
        })();
      }, 1500);
    },
    [sync]
  );
  useEffect(() => {
    return () => {
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
    };
  }, []);

  const handleCreateTask = () => {
    setMetaModal({ mode: "create" });
  };

  // 목록 드래그 → 변경된 활동지 메타만 task-meta/save-all 로 배치 갱신 (본문 불변).
  const handleReorder = (
    draggedTaskId: string,
    targetSetNumber: number,
    beforeTaskId: string | null
  ) => {
    const updates = computeListReorderUpdates(
      Object.values(tasksByTaskId),
      draggedTaskId,
      targetSetNumber,
      beforeTaskId
    );
    if (updates.length === 0) return;
    // 세트가 바뀌는 이동은 confirm 모달로 한 번 더 확인. 같은 세트 내 순서 변경은 즉시 적용.
    const dragged = tasksByTaskId[draggedTaskId];
    if (dragged && dragged.set_number !== targetSetNumber) {
      // 타깃 세트에 이미 Key 활동지가 있는지 (이동 대상 제외).
      const targetHasKey = Object.values(tasksByTaskId).some(
        (t) =>
          t &&
          t.task_id !== draggedTaskId &&
          t.set_number === targetSetNumber &&
          t.is_key_activity
      );
      // 세트당 Key 는 하나 — 이미 Key 가 있는 세트로 Key 활동지를 옮기면 이동 활동지의 Key 를 해제.
      const demoteKey = Boolean(dragged.is_key_activity) && targetHasKey;
      const finalUpdates = demoteKey
        ? updates.map((u) =>
            u.task_id === draggedTaskId ? { ...u, is_key_activity: false } : u
          )
        : updates;
      setPendingSetChange({
        updates: finalUpdates,
        taskTitle: dragged.title,
        fromSet: dragged.set_number,
        toSet: targetSetNumber,
        demoteKey,
      });
      return;
    }
    // reorderTasks 는 실패 시 store 를 롤백하고 throw — 여기선 unhandled rejection 만 방지.
    void reorderTasks(updates).catch(() => {});
  };

  const handleConfirmSetChange = () => {
    if (!pendingSetChange) return;
    const { updates } = pendingSetChange;
    setPendingSetChange(null);
    void reorderTasks(updates).catch(() => {});
  };

  const handleEditMeta = (taskId: string) => {
    const task = tasksByTaskId[taskId];
    if (!task) {
      return;
    }
    setMetaModal({
      mode: "edit",
      originalTaskId: task.task_id,
      initialValues: {
        task_id: task.task_id,
        title: task.title,
        // 모달은 미분류를 "선택 없음(undefined)"으로 표현 → 9999 를 undefined 로.
        set_number:
          task.set_number === UNCATEGORIZED_SET_NUMBER
            ? undefined
            : task.set_number,
        is_key_activity: task.is_key_activity ?? false,
      },
    });
  };

  // 메타 모달 저장 — 생성·수정 모두 task-meta/save 단건 (본문 미포함, rename 없음).
  // 생성: 빈 본문으로 시작(첫 편집·저장 시 task-content/commit). 수정: 기존 본문 보존.
  const handleMetaSubmit = async (values: TaskMetadataValues) => {
    if (!metaModal || isSavingMeta) {
      return;
    }

    const setNumber = values.set_number ?? UNCATEGORIZED_SET_NUMBER;
    const existing =
      metaModal.mode === "edit"
        ? tasksByTaskId[metaModal.originalTaskId]
        : undefined;
    // 수정: 기존 sort_order 유지. 생성: 해당 세트 맨 끝 값 부여 (NOT NULL 필수).
    const sortOrder =
      existing?.sort_order ??
      getNextSortOrderForSet(Object.values(tasksByTaskId), setNumber);

    // 수정 시 편집 중 블록 보존 (currentEditorState 우선), 생성 시 빈 본문.
    const baseState: TaskEditorState =
      metaModal.mode === "edit"
        ? cloneTaskEditorState(
            (currentEditorState &&
            currentEditorState.task_id === metaModal.originalTaskId
              ? currentEditorState
              : existing) ?? createEmptyTaskEditorState()
          )
        : createEmptyTaskEditorState();

    const nextState: TaskEditorState = {
      ...baseState,
      task_id: values.task_id,
      title: values.title,
      set_number: setNumber,
      is_key_activity: values.is_key_activity,
      sort_order: sortOrder,
    };

    setIsSavingMeta(true);
    try {
      await saveTaskMeta({
        task_id: nextState.task_id,
        title: nextState.title,
        set_number: nextState.set_number,
        is_key_activity: Boolean(nextState.is_key_activity),
        sort_order: sortOrder,
      });
      setActiveTaskId(nextState.task_id);
      setCurrentEditorState(cloneTaskEditorState(nextState));
      // 신규 활동지는 로드할 detail 이 없다 — 즉시 "로드됨" 으로 표기해 skeleton 없이 기본
      // [header] 폼을 바로 보인다 (서버 미커밋이라 hydrate 의 getTaskDetail 은 빈 본문 반환).
      markDetailLoaded(nextState.task_id);
      // hasUnsavedChanges 는 본문 커밋 전용 플래그라 meta 저장이 건드리지 않는다.
      // 미커밋 본문이 있는 채로 meta 만 저장해도 커밋 버튼이 살아있어야 한다.
      // (생성 모드는 task_id 변경 → hydrate effect 가 알아서 false 로 초기화)
      setMetaModal(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to save metadata", error);
    } finally {
      setIsSavingMeta(false);
    }
  };

  const handleChangeEditorState = (next: TaskEditorState) => {
    // 사용자 편집 — sync state 갱신 + working_store 에 debounced PUT (server-side WIP 영속화).
    sync.applyUserMutation(next);
    scheduleWorkingPut(next.task_id, next.blocks);
  };

  // Agent 가 task_patch 를 보내면 3-way merge — base(턴 시작 시점) / ours(currentTask) /
  // theirs(patch.blocks). 사용자가 같은 block 을 만지지 않았으면 그대로/auto_merge,
  // 충돌이면 banner 가 표시되고 사용자가 선택할 때까지 적용 보류.
  // BE 가 task_changed 시 자체적으로 working_store.upsert 하므로 FE 측 PUT 은 생략 (중복 방지).
  // SSE meta.version 으로 활성 task 의 workingVersionsRef 동기 — 다음 사용자 PUT 의 expected_version.
  const handleAgentTaskPatch = (
    patch: { blocks: TaskEditorState["blocks"] },
    meta?: { version?: number; revision?: string }
  ) => {
    if (typeof meta?.version === "number") {
      const id = activeTaskIdRef.current;
      if (id) workingVersionsRef.current[id] = meta.version;
    }
    // agent/BE 도 action:null 등을 생략(lean)해 직렬화하므로, raw 로 적용하면 migrate 형식인
    // V1·turnBase 와 표현이 어긋나 3-way merge 가 오작동하고 버튼이 고착된다. migrate 로
    // 정규화해 canonical 형식으로 맞춘 뒤 적용한다 (빈 patch 는 [header] 폴백을 피해 그대로).
    const normalizedBlocks =
      patch.blocks.length > 0
        ? cloneTaskEditorState({ blocks: patch.blocks }).blocks
        : patch.blocks;
    sync.applyAgentPatch({ blocks: normalizedBlocks });
  };

  // 턴 종료(정상 final / 정지 cancel / error / clear / thread 전환) 시 권위 저장소
  // (working_store)와 우측 표시를 대조한다. 스트림 중 task_patch 가 누락(정지로 큐 유실 /
  // currentTask no_op / 충돌 보류)되면 BE 에는 저장됐어도 우측이 stale 로 남는데, 여기서
  // BE blocks 를 다시 읽어 기존 경로(handleAgentTaskPatch)로 복구한다. working_store 는
  // BE 가 user PUT + agent 변경을 병합한 단일 진실원이라, 그대로 받아도 사용자 편집을
  // 덮어쓰는 게 아니다.
  //
  // markTurnEnd 는 기존처럼 *즉시* 호출해 base lifecycle 을 건드리지 않는다(다음 턴 race 방지).
  // reconcile 은 독립적인 safety net 으로, GET 이 도는 동안 표시 상태가 바뀌면(활동지 전환 /
  // 메시지 수정에 따른 revert / 새 턴 패치) stale 로 보고 적용하지 않는다 — 최신 상태 우선.
  const handleAgentTurnEnd = () => {
    sync.markTurnEnd();
    const taskId = activeTaskIdRef.current;
    if (!taskId || reconcileInFlightRef.current) return;
    const before = currentEditorStateRef.current;
    const beforeHash = before ? stableHashBlocks(before.blocks) : null;
    reconcileInFlightRef.current = true;
    void (async () => {
      try {
        const working = await getWorkingTask(taskId);
        const cur = currentEditorStateRef.current;
        const curHash = cur ? stableHashBlocks(cur.blocks) : null;
        if (
          working &&
          activeTaskIdRef.current === taskId &&
          curHash === beforeHash && // GET 도중 표시 상태 불변 (편집/전환/새 턴 없음)
          working.revision !== curHash // 그런데 working 과는 다름 → 누락분 복구
        ) {
          handleAgentTaskPatch(
            { blocks: working.blocks },
            { version: working.version, revision: working.revision }
          );
        }
      } catch {
        // best-effort — 실패해도 다음 턴 / 새로고침에서 재대조
      } finally {
        reconcileInFlightRef.current = false;
      }
    })();
  };

  const handleConflictResolve = (choice: "accept_agent" | "keep_mine") => {
    sync.resolveConflict(choice);
  };

  const handleRevertTurn = (taskBefore: TaskEditorState) => {
    sync.applyUserMutation(taskBefore);
    scheduleWorkingPut(taskBefore.task_id, taskBefore.blocks);
  };

  // 마지막 요청 수정(editLastUserTurn) 전용 — handleRevertTurn 과 달리 debounce 없이 즉시·await.
  // rewind 스트림이 복원된 blocks 를 읽도록 working_store PUT 완료까지 기다린다 (scheduleWorkingPut 본문과 동일 로직).
  const revertTaskForEdit = useCallback(
    async (taskBefore: TaskEditorState) => {
      sync.applyUserMutation(taskBefore);
      if (putTimerRef.current) clearTimeout(putTimerRef.current);
      try {
        const saved = await upsertWorkingTask(
          taskBefore.task_id,
          taskBefore.blocks,
          workingVersionsRef.current[taskBefore.task_id] ?? 0,
          "user"
        );
        workingVersionsRef.current[taskBefore.task_id] = saved.version;
        lastPutSignatureRef.current = {
          taskId: taskBefore.task_id,
          sig: stableHashBlocks(taskBefore.blocks),
        };
      } catch (err) {
        if (err instanceof WorkingTaskVersionConflictError) {
          workingVersionsRef.current[taskBefore.task_id] = err.current.version;
          if (activeTaskIdRef.current === taskBefore.task_id) {
            sync.applyAgentPatch({ blocks: err.current.blocks });
          }
          lastPutSignatureRef.current = null;
        } else {
          lastPutSignatureRef.current = null;
        }
      }
    },
    [sync]
  );

  // [커밋] = working 을 V1 (remote) 에 push + working 을 V1 과 동기화된 상태로 "고정".
  // working_store row 는 DELETE 하지 않고 V1 mirror 로 유지 — backend 의 single source 보존.
  // 다음 사용자 chat 시 backend.aread 가 V1 view 를 정상 반환.
  const handleCommit = async () => {
    if (!currentEditorState || isCommittingContent) {
      return;
    }
    setIsCommittingContent(true);
    try {
      const next = currentEditorState;
      // 본문만 commit — 캐시된 content(비-blocks 필드)에 현재 blocks 를 덮어 passthrough.
      const content = {
        ...(contentCacheRef.current[next.task_id] ?? {}),
        blocks: next.blocks,
      };
      await taskEditorApi.commitContent(next.task_id, content);
      contentCacheRef.current[next.task_id] = content;
      // store 의 V1 base blocks 갱신 (codeview/turnView/discard base).
      applyTaskBlocks(next.task_id, next.blocks);
      lastPutSignatureRef.current = null;
      // working 을 V1 과 동기화 — debounce 1.5s 안에 못 날아간 마지막 typing 까지 보장.
      try {
        const mirrored = await upsertWorkingTask(
          next.task_id,
          next.blocks,
          workingVersionsRef.current[next.task_id] ?? 0,
          "user"
        );
        workingVersionsRef.current[next.task_id] = mirrored.version;
      } catch (err) {
        if (err instanceof WorkingTaskVersionConflictError) {
          // 다른 actor 가 그 사이 working 갱신 — 현 상태를 따라간다.
          workingVersionsRef.current[next.task_id] = err.current.version;
        } else {
          // eslint-disable-next-line no-console
          console.error(
            "[TaskEditorV2] failed to mirror working after commit",
            err
          );
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[TaskEditorV2] failed to commit task", error);
    } finally {
      setIsCommittingContent(false);
    }
  };

  // [원복] = git restore . — working 의 변경을 폐기하고 V1 으로 reset. row 는 유지 (DELETE X).
  const handleDiscardWorking = async () => {
    if (!activeTaskId) return;
    const v1 = tasksByTaskId[activeTaskId];
    if (!v1) return;
    lastPutSignatureRef.current = null;
    try {
      const mirrored = await upsertWorkingTask(
        activeTaskId,
        v1.blocks,
        workingVersionsRef.current[activeTaskId] ?? 0,
        "user"
      );
      workingVersionsRef.current[activeTaskId] = mirrored.version;
    } catch (err) {
      if (err instanceof WorkingTaskVersionConflictError) {
        workingVersionsRef.current[activeTaskId] = err.current.version;
      } else {
        // eslint-disable-next-line no-console
        console.error("[TaskEditorV2] failed to restore working_task", err);
      }
    }
    setCurrentEditorState(cloneTaskEditorState(v1));
  };

  const existingTaskIds = useMemo(
    () => Object.keys(tasksByTaskId),
    [tasksByTaskId]
  );

  // 세트당 Key 활동지는 1개 — 메타 모달의 Key 충돌 검사용.
  // 수정 모드면 편집 중인 활동지 자신은 제외(자신이 그 세트의 Key 여도 충돌 아님).
  const keyActivitySetNumbers = useMemo(
    () =>
      getKeyActivitySetNumbers(
        Object.values(tasksByTaskId),
        metaModal?.mode === "edit" ? metaModal.originalTaskId : undefined
      ),
    [tasksByTaskId, metaModal]
  );

  return (
    <FullPageContainer>
      <Box
        sx={{
          display: "grid",
          position: "relative",
          gridTemplateColumns: `${taskListWidth}px minmax(0, 1fr) ${workspaceWidth}px`,
          height: "100%",
          width: "100%",
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: theme.vars.palette.Background.Level1,
        }}
      >
        <TaskListPanel
          tasks={tasksList}
          activeTaskId={activeTaskId}
          streamingTaskIds={streamingTaskIds}
          onSelectTask={setActiveTaskId}
          onCreateTask={handleCreateTask}
          onEditMeta={handleEditMeta}
          onReorder={handleReorder}
        />

        <AgentChatPanel
          taskId={activeTaskId}
          currentTask={currentEditorState}
          onTaskPatch={handleAgentTaskPatch}
          onTurnStart={sync.markTurnStart}
          onTurnEnd={handleAgentTurnEnd}
          onRevertTurn={handleRevertTurn}
          onRevertTaskBeforeEdit={revertTaskForEdit}
          onStreamingTasksChange={setStreamingTaskIds}
        />

        <Box
          onMouseDown={handleTaskListResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="활동지 리스트 패널 크기 조정"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${taskListWidth}px`,
            width: `${RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            zIndex: 2,
            "&:hover .resize-grip": {
              backgroundColor: theme.vars.palette.Content.Medium,
              opacity: 1,
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: "3px",
              height: "64px",
              borderRadius: "2px",
              backgroundColor: isResizingTaskList
                ? theme.vars.palette.Content.Medium
                : theme.vars.palette.Content.Weak,
              opacity: isResizingTaskList ? 1 : 0,
              transition: isResizingTaskList
                ? "none"
                : "opacity 0.15s, background-color 0.15s",
              pointerEvents: "none",
            }}
          />
        </Box>

        <Box
          onMouseDown={handleWorkspaceResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="우측 패널 크기 조정"
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: `${workspaceWidth}px`,
            width: `${RESIZE_HANDLE_WIDTH}px`,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            zIndex: 2,
            "&:hover .resize-grip": {
              backgroundColor: theme.vars.palette.Content.Medium,
              opacity: 1,
            },
          }}
        >
          <Box
            className="resize-grip"
            sx={{
              width: "3px",
              height: "64px",
              borderRadius: "2px",
              backgroundColor: isResizingWorkspace
                ? theme.vars.palette.Content.Medium
                : theme.vars.palette.Content.Weak,
              opacity: isResizingWorkspace ? 1 : 0,
              transition: isResizingWorkspace
                ? "none"
                : "opacity 0.15s, background-color 0.15s",
              pointerEvents: "none",
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {sync.pendingAgentPatch && (
            <ConflictBanner
              reason={sync.pendingAgentPatch.reason}
              conflictBlockIndexes={sync.pendingAgentPatch.conflictBlockIndexes}
              onAcceptAgent={() => handleConflictResolve("accept_agent")}
              onKeepMine={() => handleConflictResolve("keep_mine")}
            />
          )}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <TaskWorkspacePanel
              editorState={currentEditorState}
              isHydrating={isWorkspaceHydrating}
              showNoTasks={showNoTasks}
              onChangeEditorState={handleChangeEditorState}
              hasUnsavedChanges={hasUnsavedChanges}
              isCommitting={isCommittingContent}
              onCommit={handleCommit}
              onEditMeta={() => {
                if (activeTaskId) {
                  handleEditMeta(activeTaskId);
                }
              }}
              v1Task={activeTaskId ? tasksByTaskId[activeTaskId] : undefined}
              onDiscardWorking={handleDiscardWorking}
            />
          </Box>
        </Box>
      </Box>

      <TaskMetadataModal
        open={metaModal !== null}
        mode={metaModal?.mode ?? "create"}
        initialValues={
          metaModal?.mode === "edit" ? metaModal.initialValues : undefined
        }
        existingTaskIds={existingTaskIds}
        keyActivitySetNumbers={keyActivitySetNumbers}
        isSubmitting={isSavingMeta}
        onClose={() => {
          if (!isSavingMeta) {
            setMetaModal(null);
          }
        }}
        onSubmit={handleMetaSubmit}
        onRemove={
          metaModal?.mode === "edit"
            ? async () => {
                if (isSavingMeta || metaModal.mode !== "edit") return;
                setIsSavingMeta(true);
                try {
                  await removeTask(metaModal.originalTaskId);
                  if (activeTaskId === metaModal.originalTaskId) {
                    setActiveTaskId(undefined);
                    setCurrentEditorState(undefined);
                  }
                  setMetaModal(null);
                } catch (error) {
                  // eslint-disable-next-line no-console
                  console.error("[TaskEditorV2] failed to remove task", error);
                } finally {
                  setIsSavingMeta(false);
                }
              }
            : undefined
        }
      />

      <CustomModal
        open={pendingSetChange !== null}
        onClose={() => setPendingSetChange(null)}
        title="세트를 변경하시겠어요?"
        subtitle={
          pendingSetChange
            ? `'${pendingSetChange.taskTitle || "(제목 없음)"}' 활동지를 ${getTaskEditorSetSectionLabel(
                pendingSetChange.fromSet
              )}에서 ${getTaskEditorSetSectionLabel(
                pendingSetChange.toSet
              )}(으)로 변경합니다.${
                pendingSetChange.demoteKey
                  ? "\n\n이 세트에는 이미 Key 활동지가 있어, 이동하는 활동지의 Key 지정은 해제됩니다."
                  : ""
              }`
            : undefined
        }
        actions={[
          {
            label: "변경하기",
            onClick: handleConfirmSetChange,
            variant: "Solid",
          },
          {
            label: "취소",
            onClick: () => setPendingSetChange(null),
            variant: "Plain",
          },
        ]}
      ></CustomModal>

      <AgentDebugPanel />
    </FullPageContainer>
  );
}
